<template>
  <div>
    <div class="d-flex flex-wrap align-center ga-2 mb-3">
      <v-select
        v-model="filters.status"
        :items="statusItems"
        :label="$t('ncrm.emailMarketing.planner.filters.status')"
        hide-details
        clearable
        class="ncrm-em-planner__filter"
      />
      <v-select
        v-model="filters.category"
        :items="categories"
        :label="$t('ncrm.emailMarketing.planner.filters.category')"
        hide-details
        clearable
        class="ncrm-em-planner__filter"
      />
      <v-spacer />
      <v-btn color="tertiary" @click="openCreateDrawer">
        <v-icon start>mdi-plus</v-icon>
        {{ $t("ncrm.emailMarketing.planner.addPlan") }}
      </v-btn>
    </div>

    <v-data-table
      :headers="headers"
      :items="plans"
      :loading="loading"
      item-key="id"
      density="comfortable"
      class="elevation-0"
      :items-per-page="50"
      :row-props="rowProps"
      @click:row="onRowClick"
    >
      <template #item.title="{ item }">
        <span class="ncrm-em-planner__title">{{ item.title }}</span>
      </template>
      <template #item.category="{ item }">
        <v-chip v-if="item.category" size="x-small" color="secondary">
          {{ item.category }}
        </v-chip>
        <span v-else class="text-medium-emphasis">—</span>
      </template>
      <template #item.status="{ item }">
        <v-chip :color="statusColor(item.status)">
          {{ $t(`ncrm.emailMarketing.planner.statuses.${item.status}`) }}
        </v-chip>
      </template>
      <template #item.planned_send_at="{ item }">
        <span class="text-no-wrap">{{ formatDateTime(item.planned_send_at) }}</span>
      </template>
      <template #item.mailjet_campaign_subject="{ item }">
        <span v-if="item.mailjet_campaign_subject">{{ item.mailjet_campaign_subject }}</span>
        <span v-else class="text-medium-emphasis">—</span>
      </template>
      <template #item.actions="{ item }">
        <v-btn size="small" variant="text" color="error" @click.stop="openDeleteDialog(item)">
          <v-icon size="18">mdi-delete-outline</v-icon>
        </v-btn>
      </template>
      <template #no-data>
        <v-alert type="info" class="ma-4">
          {{ $t("ncrm.emailMarketing.planner.empty") }}
        </v-alert>
      </template>
    </v-data-table>

    <NcrmEmailMarketingPlanDrawer
      v-model="drawer.show"
      :plan="drawer.plan"
      :categories="categories"
      :campaigns="campaigns"
      :saving="drawer.saving"
      :error-message="drawer.error"
      @save="handleSave"
    />

    <v-dialog v-model="deleteDialog.show" max-width="440">
      <v-card>
        <v-card-title>{{ $t("ncrm.emailMarketing.planner.deleteTitle") }}</v-card-title>
        <v-card-text>
          {{ $t("ncrm.emailMarketing.planner.deleteConfirm", { title: deleteDialog.plan?.title || "" }) }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="deleteDialog.show = false">
            {{ $t("ncrm.emailMarketing.planner.cancel") }}
          </v-btn>
          <v-btn color="error" variant="tonal" :loading="deleteDialog.deleting" @click="confirmDelete">
            {{ $t("ncrm.emailMarketing.planner.deleteAction") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import NcrmEmailMarketingPlanDrawer from "./NcrmEmailMarketingPlanDrawer.vue";
import {
  EMAIL_MARKETING_PLAN_STATUSES,
  resolvePlanStatusColor,
  fetchEmailMarketingPlans,
  fetchEmailMarketingPlanCategories,
  createEmailMarketingPlan,
  updateEmailMarketingPlan,
  deleteEmailMarketingPlan,
} from "@/Utile/ncrmEmailMarketing.mjs";

export default {
  name: "NcrmEmailMarketingPlannerTab",
  components: { NcrmEmailMarketingPlanDrawer },
  props: {
    campaigns: { type: Array, default: () => [] },
  },
  emits: ["notify"],
  data() {
    return {
      loading: false,
      plans: [],
      categories: [],
      filters: {
        status: null,
        category: null,
      },
      drawer: {
        show: false,
        plan: null,
        saving: false,
        error: "",
      },
      deleteDialog: {
        show: false,
        plan: null,
        deleting: false,
      },
    };
  },
  computed: {
    statusItems() {
      return EMAIL_MARKETING_PLAN_STATUSES.map((status) => ({
        value: status,
        title: this.$t(`ncrm.emailMarketing.planner.statuses.${status}`),
      }));
    },
    headers() {
      return [
        { title: this.$t("ncrm.emailMarketing.planner.headers.title"), key: "title" },
        { title: this.$t("ncrm.emailMarketing.planner.headers.category"), key: "category" },
        { title: this.$t("ncrm.emailMarketing.planner.headers.status"), key: "status" },
        { title: this.$t("ncrm.emailMarketing.planner.headers.plannedSendAt"), key: "planned_send_at" },
        { title: this.$t("ncrm.emailMarketing.planner.headers.campaign"), key: "mailjet_campaign_subject", sortable: false },
        { title: "", key: "actions", sortable: false, width: 48 },
      ];
    },
  },
  watch: {
    "filters.status"() {
      this.loadPlans();
    },
    "filters.category"() {
      this.loadPlans();
    },
  },
  mounted() {
    this.loadPlans();
    this.loadCategories();
  },
  methods: {
    statusColor: resolvePlanStatusColor,
    rowProps() {
      return { style: "cursor: pointer" };
    },
    formatDateTime(value) {
      if (!value) return "—";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return String(value);
      const dd = String(date.getDate()).padStart(2, "0");
      const mm = String(date.getMonth() + 1).padStart(2, "0");
      const hh = String(date.getHours()).padStart(2, "0");
      const min = String(date.getMinutes()).padStart(2, "0");
      return `${dd}.${mm}.${date.getFullYear()}, ${hh}:${min}`;
    },
    async loadPlans() {
      this.loading = true;
      try {
        this.plans = await fetchEmailMarketingPlans({
          status: this.filters.status || undefined,
          category: this.filters.category || undefined,
        });
      } catch (error) {
        this.$emit("notify", {
          message: error?.response?.data?.error || this.$t("ncrm.emailMarketing.errors.loadFailed"),
          color: "error",
        });
      } finally {
        this.loading = false;
      }
    },
    async loadCategories() {
      try {
        this.categories = await fetchEmailMarketingPlanCategories();
      } catch (error) {
        this.categories = [];
      }
    },
    openCreateDrawer() {
      this.drawer = { show: true, plan: null, saving: false, error: "" };
    },
    onRowClick(event, { item }) {
      this.drawer = { show: true, plan: item, saving: false, error: "" };
    },
    async handleSave(payload) {
      this.drawer.saving = true;
      this.drawer.error = "";
      try {
        if (this.drawer.plan?.id) {
          await updateEmailMarketingPlan(this.drawer.plan.id, payload);
        } else {
          await createEmailMarketingPlan(payload);
        }
        this.drawer.show = false;
        this.$emit("notify", {
          message: this.$t("ncrm.emailMarketing.planner.saveSuccess"),
          color: "success",
        });
        await this.loadPlans();
        await this.loadCategories();
      } catch (error) {
        this.drawer.error =
          error?.response?.data?.error || this.$t("ncrm.emailMarketing.planner.saveError");
      } finally {
        this.drawer.saving = false;
      }
    },
    openDeleteDialog(plan) {
      this.deleteDialog = { show: true, plan, deleting: false };
    },
    async confirmDelete() {
      this.deleteDialog.deleting = true;
      try {
        await deleteEmailMarketingPlan(this.deleteDialog.plan.id);
        this.deleteDialog.show = false;
        this.$emit("notify", {
          message: this.$t("ncrm.emailMarketing.planner.deleteSuccess"),
          color: "success",
        });
        await this.loadPlans();
      } catch (error) {
        this.$emit("notify", {
          message: error?.response?.data?.error || this.$t("ncrm.emailMarketing.planner.deleteError"),
          color: "error",
        });
      } finally {
        this.deleteDialog.deleting = false;
      }
    },
  },
};
</script>

<style scoped>
.ncrm-em-planner__filter {
  max-width: 240px;
  min-width: 180px;
}

.ncrm-em-planner__title {
  font-weight: 600;
  color: var(--ncrm-color-text-strong);
}
</style>
